import { create } from 'zustand';

export type PlayerRoute =
  | 'auth'
  | 'auth.login'
  | 'auth.register'
  | 'auth.forgot'
  | 'auth.username'
  | 'lobby'
  | 'lobby.next'
  | 'lobby.legacy'
  | 'room'
  | 'milhar'
  | 'milhar.ledger'
  | 'milhar.stats'
  | 'milhar.hora'
  | 'milhar.hora.ledger'
  | 'milhar.hora.stats'
  | 'wallet'
  | 'wallet.tickets'
  | 'pedidos'
  | 'pedidos.unpaid'
  | 'leaderboard'
  | 'rewards'
  | 'avisos'
  | 'avisos.faq'
  | 'avisos.feedback'
  | 'avisos.help'
  | 'me'
  | 'me.profile'
  | 'me.vip'
  | 'me.audit'
  | 'me.limits'
  | 'me.activities'
  | 'me.invite'
  | 'me.security'
  | 'me.kyc'
  | 'me.follows'
  | 'me.history'
  | 'bet-history'
  | 'rules'
  | 'archive'
  | 'sorteios'
  | 'sorteios.history';
export type AvisosTargetView = 'avisos' | 'feedback' | 'help' | null;
// Which tab the follows page should open on. Session-only (like avisosTargetView)
// — the page has a sensible default, so this never needs to survive a reload.
export type FollowsTargetTab = 'followers' | 'following' | null;
// Which sub-tab the Rewards Center opens on. Session-only: set by deep entries
// (the welcome-coupons popup lands on Cupons), consumed once by the page.
export type RewardsTargetTab = 'earn' | 'assets' | 'hist' | null;
export type WalletTicketsTargetView = 'register' | 'participants' | 'result' | null;
export type WalletTicketsReturnTarget = { route: 'room'; roomId: string } | { route: 'sorteios' } | null;
// Where a raffle sheet was sitting when the user left it for a side excursion
// (profile / round verify). Restoring = go back to that route, then reopen the
// sheet over it, so the "close" chain from the sheet stays intact.
export type RaffleSheetOrigin = { route: PlayerRoute; roomId: string | null } | null;
export type RaffleProfileReturn = { view: 'participants' | 'result' | null; resultId: string | null; origin?: RaffleSheetOrigin } | null;
export type RaffleArchiveReturn = { resultId: string | null; origin: RaffleSheetOrigin } | null;
export type WalletTargetAction = 'deposit' | 'withdraw' | null;
export type MeSecurityTargetChannel = 'phone' | 'email' | null;
export type ToastKind = 'success' | 'error' | 'warn' | 'info' | 'loading';
export type AppToast = {
  id: number;
  text: string;
  kind: ToastKind;
  ms: number;
};

const NAV_STORAGE_KEY = 'sorteia:nav';

type NavigationSnapshot = {
  route: PlayerRoute;
  activeRoomId: string | null;
  activeOrderId: string | null;
  activeProfileUserId: string | null;
  activeArchiveRoundNumber: string | null;
  walletTargetAction: WalletTargetAction;
  meSecurityTargetChannel: MeSecurityTargetChannel;
  walletTicketsTargetView: WalletTicketsTargetView;
  walletTicketsResultId: string | null;
};

type AppState = {
  route: PlayerRoute;
  activeRoomId: string | null;
  activeOrderId: string | null;
  activeProfileUserId: string | null;
  activeArchiveRoundNumber: string | null;
  walletTargetAction: WalletTargetAction;
  // Which feedback thread the Feedback tab should open on. Not URL-carried
  // (unlike activeOrderId) — there is no deep-link format for a single
  // feedback thread yet, so this is session-only, reset the same way
  // activeOrderId is: cleared whenever the route leaves 'avisos.feedback'.
  activeFeedbackId: number | null;
  avisosTargetView: AvisosTargetView;
  followsTargetTab: FollowsTargetTab;
  rewardsTargetTab: RewardsTargetTab;
  meSecurityTargetChannel: MeSecurityTargetChannel;
  walletTicketsTargetView: WalletTicketsTargetView;
  walletTicketsResultId: string | null;
  walletTicketsReturnTarget: WalletTicketsReturnTarget;
  // When a profile is opened from a raffle sheet, remember which sheet to
  // reopen so closing the profile returns there instead of stranding the user
  // on the leaderboard (Top Players) page.
  profileReturn: RaffleProfileReturn;
  // Same idea for "Verify this round": closing the archive round detail returns
  // to the result sheet (over its original page) instead of stranding the user
  // in the archive.
  archiveReturn: RaffleArchiveReturn;
  // Wagering progress opens from the Wallet (the money card and the withdrawal
  // lock) as well as from Me. Its back button used to always land on Me, so
  // two thirds of the ways in ended somewhere the player had not been.
  auditReturn: PlayerRoute | null;
  /**
   * 关掉投注记录后该回哪。
   *
   * <p>`bet-history` 这个路由渲染的是 Me 页 + 记录浮层，所以关闭时一直硬回 `me`。
   * 从 Milhar 的「See all my bets」进去的人，关掉之后落在个人中心 —— 他没去过那里。
   * 沿用 auditReturn / profileReturn 同一套：谁把人送进来，谁负责登记回哪。
   */
  betHistoryReturn: PlayerRoute | null;
  depositCouponPrefill: number | null;
  // Which coupon the player tapped "use" on. The amount alone was not enough:
  // it dropped them into the deposit sheet with nothing selected, and with
  // several usable coupons they could easily spend the wrong one.
  depositCouponPreselectId: number | null;
  /**
   * 当前叠在页面之上的浮层，从下到上。
   *
   * <p>浮层是整屏的 UI，却一直只是 `useState`，不在历史里——所以系统返回键会
   * 直接跳过它，关掉的是它下面那一页。这个栈让每一层有自己的历史条目。
   * 它刻意不进 URL：浮层的内容（选中哪一注、哪一期）不在 hash 里，前进或刷新
   * 时「恢复」出来的只会是个空壳，比不恢复更糟。进历史的只是层数。
   */
  layerStack: string[];
  /**
   * 把层栈对齐到 `next`（从下到上）。
   *
   * <p>整组一起对账，而不是每层各自 push/pop —— 因为「同一次点击里换一层」是常态
   * （`+` 面板换成我的投注、历史抽屉换成某期详情）。逐层做的话，关旧层的
   * `history.go(-1)` 和开新层的 `pushState` 会在同一 tick 里互相打架。
   */
  syncLayers: (next: string[]) => void;
  toast: AppToast | null;
  setRoute: (route: PlayerRoute, options?: { replace?: boolean }) => void;
  /**
   * 真正的「后退」。
   *
   * <p>页面上那个 ← 一直是 `setRoute(某个写死的常量)`，也就是又压一条历史、去一个
   * 跟用户来路无关的地方。`goBack` 反过来：栈里还有我们自己的页面就弹一条，
   * 只有在栈底（深链、推送通知、新开的标签页——那些常量唯一站得住脚的场景）
   * 才退到 `fallback`，并且用 replace，免得在栈底又堆一条。
   */
  goBack: (fallback: PlayerRoute) => void;
  setDepositCouponPrefill: (amount: number | null) => void;
  setDepositCouponPreselectId: (id: number | null) => void;
  setActiveRoomId: (roomId: string | null) => void;
  setActiveOrderId: (orderId: string | null, options?: { push?: boolean }) => void;
  setActiveFeedbackId: (id: number | null) => void;
  setActiveProfileUserId: (userId: string | null, options?: { replace?: boolean }) => void;
  setActiveArchiveRoundNumber: (roundNumber: string | null, options?: { replace?: boolean }) => void;
  setWalletTargetAction: (action: WalletTargetAction, options?: { replace?: boolean }) => void;
  setAvisosTargetView: (view: AvisosTargetView) => void;
  setFollowsTargetTab: (tab: FollowsTargetTab) => void;
  setRewardsTargetTab: (tab: RewardsTargetTab) => void;
  setMeSecurityTargetChannel: (channel: MeSecurityTargetChannel, options?: { replace?: boolean }) => void;
  setWalletTicketsTarget: (view: WalletTicketsTargetView, resultId?: string | null, options?: { replace?: boolean }) => void;
  setWalletTicketsReturnTarget: (target: WalletTicketsReturnTarget) => void;
  setProfileReturn: (target: RaffleProfileReturn) => void;
  setArchiveReturn: (target: RaffleArchiveReturn) => void;
  setAuditReturn: (route: PlayerRoute | null) => void;
  setBetHistoryReturn: (route: PlayerRoute | null) => void;
  // Restore a raffle sheet at its remembered origin: route back first (so the
  // page under the sheet — and the sheet's own close destination — is the one
  // the user actually came from), then reopen the sheet.
  reopenRaffleSheetAt: (origin: RaffleSheetOrigin, view: Exclude<WalletTicketsTargetView, null>, resultId?: string | null) => void;
  openWalletTicketsFlow: (view: Exclude<WalletTicketsTargetView, null>, resultId?: string | null, returnTarget?: WalletTicketsReturnTarget) => void;
  dismissWalletTicketsFlow: () => void;
  showToast: (text: string, kind?: ToastKind, options?: { ms?: number }) => void;
  clearToast: (id?: number) => void;
  hydrateFromBrowser: () => void;
};

const initialNavigation = readInitialNavigation();

export const useAppStore = create<AppState>((set, get) => ({
  route: initialNavigation.route,
  activeRoomId: initialNavigation.activeRoomId,
  activeOrderId: initialNavigation.activeOrderId,
  activeFeedbackId: null,
  activeProfileUserId: initialNavigation.activeProfileUserId,
  activeArchiveRoundNumber: initialNavigation.activeArchiveRoundNumber,
  walletTargetAction: initialNavigation.walletTargetAction,
  avisosTargetView: null,
  followsTargetTab: null,
  rewardsTargetTab: null,
  meSecurityTargetChannel: initialNavigation.meSecurityTargetChannel,
  walletTicketsTargetView: initialNavigation.walletTicketsTargetView,
  walletTicketsResultId: initialNavigation.walletTicketsResultId,
  walletTicketsReturnTarget: null,
  profileReturn: null,
  archiveReturn: null,
  auditReturn: null,
  betHistoryReturn: null,
  depositCouponPrefill: null,
  depositCouponPreselectId: null,
  layerStack: [],
  syncLayers: (next) => {
    const current = get().layerStack;
    if (current.length === next.length && current.every((id, i) => id === next[i])) return;
    set({ layerStack: next });
    if (isPrefix(current, next)) {
      // Layers opened on top of what was already there: one entry each, so back
      // unwinds them one at a time.
      for (let depth = current.length + 1; depth <= next.length; depth += 1) syncLayerDepth(depth, false);
      return;
    }
    if (isPrefix(next, current)) {
      // Layers closed from the top (the sheet's own X, or a lower one closing and
      // taking the ones above it). Give the entries back instead of stranding
      // them, or the next system-back press lands on a dead entry.
      try {
        window.history.go(next.length - current.length);
      } catch {
        /* embedded browsers that reject history writes: the store is authoritative */
      }
      return;
    }
    // A swap in one gesture — the plus menu becoming the bet history, the history
    // drawer becoming a single draw. One layer replaces another at the same
    // depth, so the entry is rewritten rather than pushed: back still closes it,
    // and the menu it replaced does not come back on the way out.
    syncLayerDepth(next.length, true);
  },
  toast: null,
  setDepositCouponPrefill: (amount) => set({ depositCouponPrefill: amount }),
  setDepositCouponPreselectId: (id) => set({ depositCouponPreselectId: id }),
  setRoute: (route, options) => set((state) => {
    const activeRoomId = route === 'room' ? state.activeRoomId : null;
    const activeOrderId = keepsActiveOrderId(route) ? state.activeOrderId : null;
    const activeFeedbackId = route === 'avisos.feedback' ? state.activeFeedbackId : null;
    const activeProfileUserId = route === 'leaderboard' ? state.activeProfileUserId : null;
    const activeArchiveRoundNumber = route === 'archive' ? state.activeArchiveRoundNumber : null;
    const walletTargetAction = route === 'wallet' ? state.walletTargetAction : null;
    const meSecurityTargetChannel = route === 'me.security' ? state.meSecurityTargetChannel : null;
    const walletTicketsTargetView = route === 'wallet.tickets' ? state.walletTicketsTargetView : null;
    const walletTicketsResultId = route === 'wallet.tickets' ? state.walletTicketsResultId : null;
    const walletTicketsReturnTarget = route === 'wallet.tickets' ? state.walletTicketsReturnTarget : null;
    const returnTargets = keepReturnTargetsFor(route, state);
    // Leaving the page takes its sheets with it. The entry syncBrowserNavigation
    // is about to write carries no layerDepth, i.e. 0 — so this keeps the store
    // and that entry agreeing.
    const layerStack: string[] = [];
    syncBrowserNavigation(route, activeRoomId, activeOrderId, activeProfileUserId, activeArchiveRoundNumber, walletTargetAction, meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId, options?.replace);
    persistNavigation(route, activeRoomId, activeOrderId, activeProfileUserId, activeArchiveRoundNumber, walletTargetAction, meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId);
    return { route, activeRoomId, activeOrderId, activeFeedbackId, activeProfileUserId, activeArchiveRoundNumber, walletTargetAction, meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId, walletTicketsReturnTarget, layerStack, ...returnTargets };
  }),
  goBack: (fallback) => {
    if (typeof window !== 'undefined' && currentHistoryIndex() > 0) {
      // popstate -> hydrateFromBrowser restores the route from the entry we land
      // on, so there is nothing to set here. Anything this function set would be
      // overwritten a tick later anyway.
      try {
        window.history.back();
        return;
      } catch {
        // Embedded browsers that reject history writes fall through to the
        // fallback rather than stranding the user on a page with no way out.
      }
    }
    get().setRoute(fallback, { replace: true });
  },
  setActiveRoomId: (activeRoomId) => set((state) => {
    if (state.route === 'room') syncBrowserNavigation(state.route, activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId, true);
    persistNavigation(state.route, activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId);
    return { activeRoomId };
  }),
  setActiveOrderId: (activeOrderId, options) => set((state) => {
    if (keepsActiveOrderId(state.route)) syncBrowserNavigation(state.route, state.activeRoomId, activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId, !options?.push);
    persistNavigation(state.route, state.activeRoomId, activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId);
    return { activeOrderId };
  }),
  // Session-only, no browser/localStorage sync — see the activeFeedbackId
  // field comment. The Feedback tab reads it once to know which thread to
  // open, the same way avisosTargetView is consumed once and cleared.
  setActiveFeedbackId: (activeFeedbackId) => set({ activeFeedbackId }),
  setActiveProfileUserId: (userId, options) => set((state) => {
    const activeProfileUserId = normalizeRouteValue(userId || null);
    if (state.route === 'leaderboard') {
      syncBrowserNavigation(state.route, state.activeRoomId, state.activeOrderId, activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId, options?.replace);
    }
    persistNavigation(state.route, state.activeRoomId, state.activeOrderId, activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId);
    return { activeProfileUserId };
  }),
  setActiveArchiveRoundNumber: (roundNumber, options) => set((state) => {
    const activeArchiveRoundNumber = normalizeRouteValue(roundNumber || null);
    if (state.route === 'archive') {
      syncBrowserNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId, options?.replace);
    }
    persistNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId);
    return { activeArchiveRoundNumber };
  }),
  setWalletTargetAction: (action, options) => set((state) => {
    const walletTargetAction = normalizeWalletTargetAction(action);
    if (state.route === 'wallet') {
      syncBrowserNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId, options?.replace);
    }
    persistNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, walletTargetAction, state.meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId);
    return { walletTargetAction };
  }),
  setAvisosTargetView: (avisosTargetView) => set({ avisosTargetView }),
  setFollowsTargetTab: (followsTargetTab) => set({ followsTargetTab }),
  setRewardsTargetTab: (rewardsTargetTab) => set({ rewardsTargetTab }),
  setMeSecurityTargetChannel: (channel, options) => set((state) => {
    const meSecurityTargetChannel = normalizeMeSecurityTargetChannel(channel);
    if (state.route === 'me.security') {
      syncBrowserNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId, options?.replace);
    }
    persistNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, meSecurityTargetChannel, state.walletTicketsTargetView, state.walletTicketsResultId);
    return { meSecurityTargetChannel };
  }),
  setWalletTicketsTarget: (walletTicketsTargetView, resultId, options) => set((state) => {
    const walletTicketsResultId = walletTicketsTargetView === 'result' ? normalizeRouteValue(resultId || null) : null;
    if (state.route === 'wallet.tickets') {
      syncBrowserNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId, options?.replace);
    }
    persistNavigation(state.route, state.activeRoomId, state.activeOrderId, state.activeProfileUserId, state.activeArchiveRoundNumber, state.walletTargetAction, state.meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId);
    return { walletTicketsTargetView, walletTicketsResultId };
  }),
  setWalletTicketsReturnTarget: (target) => set({
    walletTicketsReturnTarget: normalizeWalletTicketsReturnTarget(target),
  }),
  setProfileReturn: (target) => set({ profileReturn: target }),
  setArchiveReturn: (target) => set({ archiveReturn: target }),
  setAuditReturn: (route) => set({ auditReturn: route }),
  setBetHistoryReturn: (route) => set({ betHistoryReturn: route }),
  reopenRaffleSheetAt: (origin, view, resultId) => {
    if (origin?.route === 'room' && origin.roomId) get().setActiveRoomId(origin.roomId);
    get().setRoute(origin?.route || 'sorteios', { replace: true });
    get().openWalletTicketsFlow(view, resultId ?? null);
  },
  openWalletTicketsFlow: (view, resultId, returnTarget = null) => {
    // Viewing a RESULT is a modal over wherever you already are (the overlay is
    // mounted globally). Never navigate for it, so closing returns the user
    // exactly where they opened it — Wallet, Updates, a room, or Sorteios.
    if (view === 'result') {
      set({ walletTicketsReturnTarget: null });
      get().setWalletTicketsTarget('result', resultId);
      return;
    }
    // Register/participants belong to the Sorteios draw venue. Route first, then
    // set the target: setRoute() clears walletTicketsTargetView for any route
    // other than 'wallet.tickets', so the target must be written AFTER the
    // navigation. Skip the re-route when already on Sorteios to avoid a visible
    // double-navigate.
    if (get().route !== 'sorteios') get().setRoute('sorteios');
    set({ walletTicketsReturnTarget: normalizeWalletTicketsReturnTarget(returnTarget) });
    get().setWalletTicketsTarget(view, resultId);
  },
  dismissWalletTicketsFlow: () => {
    const returnTarget = get().walletTicketsReturnTarget;
    get().setWalletTicketsTarget(null, null, { replace: true });
    set({ walletTicketsReturnTarget: null });
    if (returnTarget?.route === 'room') {
      get().setActiveRoomId(returnTarget.roomId);
      get().setRoute('room', { replace: true });
    } else if (returnTarget?.route === 'sorteios') {
      get().setRoute('sorteios', { replace: true });
    }
  },
  showToast: (text, kind = 'info', options) => set({
    toast: {
      id: Date.now() + Math.floor(Math.random() * 1000),
      text,
      kind,
      ms: options?.ms ?? (kind === 'loading' ? 30_000 : 2600),
    },
  }),
  clearToast: (id) => set((state) => {
    if (id != null && state.toast?.id !== id) return {};
    return { toast: null };
  }),
  hydrateFromBrowser: () => {
    // Layers first, and before the unchanged-route short-circuit below: opening
    // a sheet does not move the URL, so a back press out of one produces a
    // popstate where every route field is identical. Reconciling after that
    // check would mean the sheet never closes.
    const targetDepth = readLayerDepth();
    const openLayers = get().layerStack;
    if (openLayers.length > targetDepth) {
      set({ layerStack: openLayers.slice(0, targetDepth) });
    } else if (openLayers.length < targetDepth) {
      // We landed on an entry that remembers layers we cannot bring back (their
      // contents were never in the URL). Rewrite the entry to what is actually
      // open, or the next open/close cycle on this page compares against a stale
      // number and stops closing.
      syncLayerDepth(openLayers.length, true);
    }

    const next = readBrowserNavigation();
    if (!next) return;
    const current = get();
    const walletTicketsReturnTarget = next.route === 'wallet.tickets'
      ? current.walletTicketsReturnTarget
      : null;
    if (
      current.route === next.route &&
      current.activeRoomId === next.activeRoomId &&
      current.activeOrderId === next.activeOrderId &&
      current.activeProfileUserId === next.activeProfileUserId &&
      current.activeArchiveRoundNumber === next.activeArchiveRoundNumber &&
      current.walletTargetAction === next.walletTargetAction &&
      current.meSecurityTargetChannel === next.meSecurityTargetChannel &&
      current.walletTicketsTargetView === next.walletTicketsTargetView &&
      current.walletTicketsResultId === next.walletTicketsResultId &&
      current.walletTicketsReturnTarget === walletTicketsReturnTarget
    ) return;
    persistNavigation(next.route, next.activeRoomId, next.activeOrderId, next.activeProfileUserId, next.activeArchiveRoundNumber, next.walletTargetAction, next.meSecurityTargetChannel, next.walletTicketsTargetView, next.walletTicketsResultId);
    set({
      // Same rule as setRoute: a return target only survives a navigation that
      // lands on the page that consumes it. Without this the system back button
      // — which never reaches any page's close handler — leaves the value behind
      // for the next, unrelated close to act on.
      ...keepReturnTargetsFor(next.route, current),
      route: next.route,
      activeRoomId: next.activeRoomId,
      activeOrderId: next.activeOrderId,
      activeProfileUserId: next.activeProfileUserId,
      activeArchiveRoundNumber: next.activeArchiveRoundNumber,
      walletTargetAction: next.walletTargetAction,
      meSecurityTargetChannel: next.meSecurityTargetChannel,
      walletTicketsTargetView: next.walletTicketsTargetView,
      walletTicketsResultId: next.walletTicketsResultId,
      walletTicketsReturnTarget,
    });
  },
}));

// The four "where did the user come from" fields each belong to exactly one
// page, and each is written immediately before navigating into that page. So a
// navigation keeps one only when it is landing on that page — every other
// destination means the user left without the consuming close handler ever
// running, and a value kept past that point does not restore anything: it
// teleports the next, unrelated close somewhere the user has never been.
// (`walletTicketsReturnTarget` has always been handled this way inline; these
// four were not, which is what made "close a profile on the leaderboard, land
// in a room you left ten minutes ago" possible.)
const RETURN_TARGET_OWNERS = {
  profileReturn: 'leaderboard',
  archiveReturn: 'archive',
  auditReturn: 'me.audit',
  betHistoryReturn: 'bet-history',
} as const satisfies Record<string, PlayerRoute>;

function keepReturnTargetsFor(route: PlayerRoute, state: Pick<AppState, keyof typeof RETURN_TARGET_OWNERS>) {
  return {
    profileReturn: route === RETURN_TARGET_OWNERS.profileReturn ? state.profileReturn : null,
    archiveReturn: route === RETURN_TARGET_OWNERS.archiveReturn ? state.archiveReturn : null,
    auditReturn: route === RETURN_TARGET_OWNERS.auditReturn ? state.auditReturn : null,
    betHistoryReturn: route === RETURN_TARGET_OWNERS.betHistoryReturn ? state.betHistoryReturn : null,
  };
}

function isPrefix(shorter: string[], longer: string[]) {
  return shorter.length <= longer.length && shorter.every((id, i) => id === longer[i]);
}

// Writes how many layers are open onto a history entry, leaving the URL and the
// route payload alone. `push` creates the entry a newly opened layer needs;
// otherwise it heals the entry we are already sitting on.
function syncLayerDepth(layerDepth: number, replace: boolean) {
  if (typeof window === 'undefined') return;
  try {
    const state = { ...(window.history.state as object | null), layerDepth };
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (replace) window.history.replaceState(state, '', url);
    else window.history.pushState({ ...state, idx: currentHistoryIndex() + 1 }, '', url);
  } catch {
    /* embedded browsers that reject history writes: the store is authoritative */
  }
}

function readLayerDepth(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const state = window.history.state as { layerDepth?: number } | null;
    return typeof state?.layerDepth === 'number' && state.layerDepth > 0 ? state.layerDepth : 0;
  } catch {
    return 0;
  }
}

function currentHistoryIndex(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const state = window.history.state as { idx?: number } | null;
    return typeof state?.idx === 'number' && state.idx >= 0 ? state.idx : 0;
  } catch {
    return 0;
  }
}

function readInitialNavigation(): NavigationSnapshot {
  const fromBrowser = readBrowserNavigation();
  if (fromBrowser) return fromBrowser;

  if (typeof window === 'undefined') return emptyNavigationSnapshot();
  try {
    const raw = window.localStorage.getItem(NAV_STORAGE_KEY);
    if (!raw) return emptyNavigationSnapshot();
    const saved = JSON.parse(raw) as {
      route?: string;
      activeRoomId?: string | null;
      currentRoomId?: string | null;
      activeOrderId?: string | null;
      activeProfileUserId?: string | null;
      activeArchiveRoundNumber?: string | null;
      walletTargetAction?: WalletTargetAction;
      meSecurityTargetChannel?: MeSecurityTargetChannel;
      walletTicketsTargetView?: WalletTicketsTargetView;
      walletTicketsResultId?: string | null;
    };
    const route = normalizeRouteName(saved.route);
    return {
      route: route || 'lobby',
      activeRoomId: normalizeRoomId(saved.activeRoomId || saved.currentRoomId || null),
      activeOrderId: route && keepsActiveOrderId(route) ? normalizeOrderId(saved.activeOrderId || null) : null,
      activeProfileUserId: route === 'leaderboard' ? normalizeRouteValue(saved.activeProfileUserId || null) : null,
      activeArchiveRoundNumber: route === 'archive' ? normalizeRouteValue(saved.activeArchiveRoundNumber || null) : null,
      walletTargetAction: route === 'wallet' ? normalizeWalletTargetAction(saved.walletTargetAction || null) : null,
      meSecurityTargetChannel: route === 'me.security' ? normalizeMeSecurityTargetChannel(saved.meSecurityTargetChannel || null) : null,
      walletTicketsTargetView: route === 'wallet.tickets' ? normalizeWalletTicketsTarget(saved.walletTicketsTargetView || null) : null,
      walletTicketsResultId: route === 'wallet.tickets' && saved.walletTicketsTargetView === 'result' ? normalizeRouteValue(saved.walletTicketsResultId || null) : null,
    };
  } catch {
    return emptyNavigationSnapshot();
  }
}

function emptyNavigationSnapshot(): NavigationSnapshot {
  return {
    route: 'lobby',
    activeRoomId: null,
    activeOrderId: null,
    activeProfileUserId: null,
    activeArchiveRoundNumber: null,
    walletTargetAction: null,
    meSecurityTargetChannel: null,
    walletTicketsTargetView: null,
    walletTicketsResultId: null,
  };
}

function readBrowserNavigation(): NavigationSnapshot | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const hashBody = hash.replace(/^#\/?/, '');
  const [pathPart, queryPart = ''] = hashBody.split('?');
  const query = safeSearchParams(queryPart);
  const segments = pathPart.split('/').filter(Boolean).map((segment) => safeDecode(segment));
  if (!segments.length) {
    // A bare site address always opens the hall, even when an earlier visit
    // saved a wallet or room route. Replace the entry so Back does not return
    // to an empty URL; keep attribution parameters on the original address.
    syncBrowserNavigation('lobby', null, null, null, null, null, null, null, null, true);
    return emptyNavigationSnapshot();
  }
  const route = normalizeRouteName(segments[0], segments[1]);
  if (!route) return null;
  const walletTicketsTarget = parseWalletTicketsTarget(segments, route, query);
  return {
    route,
    activeRoomId: route === 'room' ? normalizeRoomId(segments[1] || null) : null,
    activeOrderId: route === 'pedidos' ? normalizeOrderId(segments[1] || null)
      : route === 'pedidos.unpaid' ? normalizeOrderId(segments[2] || query.get('order') || null)
      : route === 'me.history' ? normalizeOrderId(historyOrderIdFromSegments(segments))
      : null,
    activeProfileUserId: parseLeaderboardProfileId(segments, route),
    activeArchiveRoundNumber: parseArchiveRoundNumber(segments, route),
    walletTargetAction: parseWalletTargetAction(segments, route),
    meSecurityTargetChannel: parseMeSecurityTargetChannel(segments, route),
    walletTicketsTargetView: walletTicketsTarget.view,
    walletTicketsResultId: walletTicketsTarget.resultId,
  };
}

// The order id sits at a different segment depending on which hash shape
// hydrated it: the legacy "#/pedidos/<id>" (also "orders"/"pedido"/"order")
// puts it right after the root, while the canonical "#/me/history/<id>"
// puts it after both "me" and "history".
function historyOrderIdFromSegments(segments: string[]): string | null {
  return ['pedidos', 'pedido', 'orders', 'order'].includes(routeAliasKey(segments[0])) ? segments[1] || null : segments[2] || null;
}

function keepsActiveOrderId(route: PlayerRoute) {
  return route === 'pedidos' || route === 'pedidos.unpaid' || route === 'me.history';
}

function normalizeRouteName(route: string | undefined, param?: string): PlayerRoute | null {
  const key = routeAliasKey(route);
  const sub = routeAliasKey(param);
  if (!key) return null;
  // Preview address for the redesigned hall. Deliberately its own route: the
  // current lobby stays untouched until the redesign is signed off.
  if (key === 'hall-next' || key === 'lobby.next' || key === 'hall2') return 'lobby.next';
  // The hall this replaced, kept reachable while the new one settles.
  if (key === 'hall-legacy' || key === 'lobby.legacy') return 'lobby.legacy';
  if (key === 'hall' || key === 'lobby' || key === 'salas') return 'lobby';
  if (key === 'auth' || key === 'login') return 'auth.login';
  if (key === 'register' || key === 'signup' || key === 'cadastro') return 'auth.register';
  if (key === 'forgot' || key === 'forgot-password' || key === 'reset-password') return 'auth.forgot';
  if (['auth.login', 'auth.register', 'auth.forgot', 'auth.username'].includes(key)) return key as PlayerRoute;
  if (key === 'username' && sub === '') return 'auth.username';
  if (key.startsWith('auth.')) return 'auth.login';
  if (['wallet', 'carteira', 'billetera'].includes(key)) {
    if (['tickets', 'bilhetes', 'raffle', 'sorteio', 'sorteio-horario'].includes(sub)) return 'wallet.tickets';
    if (['orders', 'pedidos', 'pedido'].includes(sub)) return 'pedidos';
    return 'wallet';
  }
  if (['tickets', 'bilhetes', 'raffle', 'sorteio', 'sorteio-horario', 'raffle-result', 'raffle-reveal', 'open-raffle-result', 'open-raffle-reveal'].includes(key)) return 'wallet.tickets';
  if (['pedidos', 'pedido', 'orders', 'order'].includes(key)) return sub === 'unpaid' ? 'pedidos.unpaid' : sub ? 'me.history' : 'pedidos';
  if (key === 'pedidos.unpaid' || key === 'me.history') return key; // persisted-nav restore passes the dotted name as a single value
  if (['top', 'ranking', 'leaderboard', 'tournament'].includes(key)) return 'leaderboard';
  if (key === 'me' && sub) {
    const meRoute = meRouteFromAlias(sub);
    if (meRoute) return meRoute;
  }
  if (['rewards', 'perks', 'benefits', 'welfare'].includes(key)) return 'rewards';
  // Checked before the generic meRouteFromAlias(key) lookup below: bare
  // "#/history" is the long-standing draw-archive shortcut, and 'history' is
  // also one of me.history's aliases (for "#/me/history") — without this
  // ordering the bare form would get shadowed and land on Transactions
  // instead of the archive.
  if (['archive', 'draw-archive', 'history', 'historico-sorteios', 'historial-sorteos'].includes(key)) return 'archive';
  const directMeRoute = meRouteFromAlias(key);
  if (directMeRoute) return directMeRoute;
  // Old FAQ deep links/bookmarks (both hash-segment "#/avisos/faq" and a bare
  // "#/faq") keep resolving — they now land on the Help tab, not a dead
  // 'avisos.faq' route. The dot form also covers a persisted-nav restore from
  // before this route was renamed (saved.route === 'avisos.faq' round-trips
  // through here with no `sub`, since it is passed as a single value, not a
  // hash split into segments).
  if (key === 'avisos' && sub === 'faq') return 'avisos.help';
  if (key === 'faq') return 'avisos.help';
  if (key === 'avisos.faq') return 'avisos.help';
  if (key === 'avisos' && sub === 'help') return 'avisos.help';
  if (key === 'avisos' && sub === 'feedback') return 'avisos.feedback';
  // Old support-drawer deep links/bookmarks (dot form from a persisted-nav
  // restore, same mechanism as the avisos.faq case above) also land here now.
  if (key === 'me.support') return 'avisos.help';
  // `#/rules/milhar` 是彩票规则初稿单独成页时的地址。那一段现在并进了本页
  // PART 3，所以子段忽略掉、一律落到 rules —— 发出去的链接不要变成死链。
  if (['rules', 'fairness', 'provably-fair', 'verify', 'transparency'].includes(key)) return 'rules';
  if (key === 'sorteios.history') return 'sorteios.history';
  if (['sorteios', 'sorteio', 'draws', 'lucky-draw', 'sorteos'].includes(key)) {
    // "/sorteios/fair" used to be a second how-the-draw-works page. It taught the
    // algorithm with a hardcoded example while the rules page teaches the same
    // thing with a real settled round — two doors, one job, and the example on
    // this one was invented (2026-09-18 PM retired it). The aliases still
    // resolve so old links and saved navigation land somewhere real.
    // (and its pt/es spellings) → the past-draws list; bare → Draws.
    if (['fair', 'fairness', 'como-funciona', 'how-it-works'].includes(sub)) return 'rules';
    if (['history', 'historico', 'historial'].includes(sub)) return 'sorteios.history';
    return 'sorteios';
  }
  // 点式也接受：持久化导航恢复时 route 是作为整串传进来的，不会被 hash 切段。
  if (key === 'milhar.ledger') return 'milhar.ledger';
  if (key === 'milhar.stats') return 'milhar.stats';
  if (key === 'milhar.hora') return 'milhar.hora';
  if (key === 'milhar.hora.ledger') return 'milhar.hora.ledger';
  if (key === 'milhar.hora.stats') return 'milhar.hora.stats';
  if (key === 'milhar-hora') {
    if (['ledger', 'acumulado', 'transparencia'].includes(sub)) return 'milhar.hora.ledger';
    return ['stats', 'estatisticas', 'estadisticas', 'resultados'].includes(sub) ? 'milhar.hora.stats' : 'milhar.hora';
  }
  if (['milhar', 'quatro-digitos'].includes(key)) {
    if (['ledger', 'acumulado', 'transparencia'].includes(sub)) return 'milhar.ledger';
    return ['stats', 'estatisticas', 'estadisticas', 'resultados'].includes(sub) ? 'milhar.stats' : 'milhar';
  }
  if (['room', 'rewards', 'avisos', 'avisos.faq', 'avisos.help', 'avisos.feedback', 'me', 'me.profile', 'me.vip', 'me.audit', 'me.limits', 'me.activities', 'me.invite', 'me.security', 'me.kyc', 'bet-history'].includes(key)) return key as PlayerRoute;
  return null;
}

function routeAliasKey(value: string | undefined) {
  return String(value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/_/g, '-');
}

function meRouteFromAlias(value: string): PlayerRoute | null {
  if (['profile', 'perfil', 'account', 'conta', 'me-profile'].includes(value)) return 'me.profile';
  if (['vip', 'me-vip'].includes(value)) return 'me.vip';
  if (['audit', 'audits', 'wagering', 'wagering-progress', 'requisitos', 'requisitos-aposta'].includes(value)) return 'me.audit';
  if (['limits', 'limites', 'limites-jogo', 'responsible', 'responsible-gambling'].includes(value)) return 'me.limits';
  if (['follows', 'following', 'followers', 'seguindo', 'seguidores', 'social'].includes(value)) return 'me.follows';
  if (['history', 'historico', 'historial', 'transactions', 'orders', 'transaction-history'].includes(value)) return 'me.history';
  if (['activities', 'activity', 'atividades', 'actividades', 'promos', 'promocoes', 'redpacket', 'red-packet', 'bags', 'bag', 'me-bags'].includes(value)) return 'me.activities';
  if (['invite', 'invites', 'referral', 'convite', 'invitacion'].includes(value)) return 'me.invite';
  if (['security', 'seguranca', 'seguridad'].includes(value)) return 'me.security';
  if (['kyc', 'identity', 'identity-verification', 'verify-identity'].includes(value)) return 'me.kyc';
  // The old support drawer is gone — help now lives only on the Updates
  // page's Help tab. Old links/bookmarks to "support"/"help" (bare, or as
  // "me/support") still resolve, just to the new destination.
  if (['support', 'suporte', 'soporte', 'help'].includes(value)) return 'avisos.help';
  return null;
}

function normalizeRoomId(roomId: string | null): string | null {
  if (!roomId) return null;
  return roomId.startsWith('room-') ? roomId : `room-${roomId}`;
}

function normalizeOrderId(orderId: string | null): string | null {
  const value = String(orderId || '').trim();
  return value ? value : null;
}

function normalizeRouteValue(value: string | null): string | null {
  const normalized = String(value || '').trim();
  return normalized ? normalized : null;
}

function normalizeWalletTicketsTarget(value: string | null): WalletTicketsTargetView {
  return value === 'register' || value === 'participants' || value === 'result' ? value : null;
}

function normalizeWalletTicketsReturnTarget(target: WalletTicketsReturnTarget): WalletTicketsReturnTarget {
  if (target?.route === 'sorteios') return { route: 'sorteios' };
  if (target?.route !== 'room') return null;
  const roomId = normalizeRoomId(target.roomId);
  return roomId ? { route: 'room', roomId } : null;
}

function normalizeWalletTargetAction(value: string | null): WalletTargetAction {
  return value === 'deposit' || value === 'withdraw' ? value : null;
}

function normalizeMeSecurityTargetChannel(value: string | null): MeSecurityTargetChannel {
  return value === 'phone' || value === 'email' ? value : null;
}

function parseWalletTargetAction(segments: string[], route: PlayerRoute): WalletTargetAction {
  if (route !== 'wallet') return null;
  const detailSegments = isWalletRoot(segments[0]) ? segments.slice(1) : segments;
  const action = routeAliasKey(detailSegments[0]);
  if (['deposit', 'pix-deposit', 'deposito', 'recharge', 'topup'].includes(action)) return 'deposit';
  if (['withdraw', 'pix-withdraw', 'saque', 'cashout'].includes(action)) return 'withdraw';
  return null;
}

function parseWalletTicketsTarget(segments: string[], route: PlayerRoute, query?: URLSearchParams): { view: WalletTicketsTargetView; resultId: string | null } {
  if (route !== 'wallet.tickets') return { view: null, resultId: null };
  const detailSegments = walletTicketsDetailSegments(segments);
  const view = routeAliasKey(detailSegments[0]);
  const root = routeAliasKey(segments[0]);
  const resultId = normalizeRouteValue(detailSegments[1] || walletTicketsResultIdFromQuery(query) || null);
  if (['raffle-result', 'raffle-reveal', 'open-raffle-result', 'open-raffle-reveal'].includes(root)) {
    return { view: 'result', resultId: normalizeRouteValue(detailSegments[0] || walletTicketsResultIdFromQuery(query) || null) };
  }
  if (['raffle', 'register', 'registration', 'sorteio'].includes(view)) return { view: 'register', resultId: null };
  if (['participants', 'entries', 'participantes'].includes(view)) return { view: 'participants', resultId: null };
  if (['result', 'reveal', 'resultado'].includes(view)) return { view: 'result', resultId };
  return { view: null, resultId: null };
}

function walletTicketsResultIdFromQuery(query?: URLSearchParams) {
  if (!query) return null;
  return query.get('event_id') ||
    query.get('eventId') ||
    query.get('raffle_event_id') ||
    query.get('raffleEventId') ||
    query.get('event') ||
    query.get('id');
}

function isWalletRoot(value: string | undefined) {
  return ['wallet', 'carteira', 'billetera'].includes(routeAliasKey(value));
}

function walletTicketsDetailSegments(segments: string[]) {
  if (isWalletRoot(segments[0])) return segments.slice(['tickets', 'bilhetes'].includes(routeAliasKey(segments[1])) ? 2 : 1);
  return segments.slice(1);
}

function parseLeaderboardProfileId(segments: string[], route: PlayerRoute) {
  if (route !== 'leaderboard') return null;
  const root = routeAliasKey(segments[0]);
  const detailSegments = ['leaderboard', 'top', 'ranking', 'tournament'].includes(root) ? segments.slice(1) : segments;
  const mode = routeAliasKey(detailSegments[0]);
  if (mode === 'user' || mode === 'profile' || mode === 'player') {
    return normalizeRouteValue(detailSegments[1] || null);
  }
  return null;
}

function parseArchiveRoundNumber(segments: string[], route: PlayerRoute) {
  if (route !== 'archive') return null;
  const root = routeAliasKey(segments[0]);
  const detailSegments = ['archive', 'draw-archive', 'history', 'historico-sorteios', 'historial-sorteos'].includes(root) ? segments.slice(1) : segments;
  const mode = routeAliasKey(detailSegments[0]);
  if (mode === 'round' || mode === 'draw') return normalizeRouteValue(detailSegments[1] || null);
  if (/^\d+$/.test(detailSegments[0] || '')) return normalizeRouteValue(detailSegments[0] || null);
  return null;
}

function parseMeSecurityTargetChannel(segments: string[], route: PlayerRoute): MeSecurityTargetChannel {
  if (route !== 'me.security') return null;
  const detailSegments = segments[0] === 'me' ? segments.slice(2) : segments.slice(1);
  const channel = routeAliasKey(detailSegments[0]);
  if (['phone', 'telefone', 'tel', 'mobile', 'celular'].includes(channel)) return 'phone';
  if (['email', 'e-mail', 'mail'].includes(channel)) return 'email';
  return null;
}

function safeDecode(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function safeSearchParams(value: string) {
  try {
    return new URLSearchParams(value);
  } catch {
    return new URLSearchParams();
  }
}

function syncBrowserNavigation(
  route: PlayerRoute,
  activeRoomId: string | null,
  activeOrderId: string | null,
  activeProfileUserId: string | null,
  activeArchiveRoundNumber: string | null,
  walletTargetAction: WalletTargetAction,
  meSecurityTargetChannel: MeSecurityTargetChannel,
  walletTicketsTargetView: WalletTicketsTargetView,
  walletTicketsResultId: string | null,
  replace = false,
) {
  if (typeof window === 'undefined') return;
  const hash = buildHash(route, activeRoomId, activeOrderId, activeProfileUserId, activeArchiveRoundNumber, walletTargetAction, meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId);
  const url = `${window.location.pathname}${window.location.search}${hash}`;
  try {
    // How deep into OUR OWN history this entry sits. goBack() needs it to tell
    // "there is a page of ours behind this one" from "behind this is whatever
    // site the user was on before" — the browser exposes history.length, which
    // counts both and so cannot answer that. Kept in history.state rather than
    // mirrored in the store because history.state survives a reload: refresh
    // five pages deep and back still works.
    const idx = replace ? currentHistoryIndex() : currentHistoryIndex() + 1;
    const state = { r: route, idx, activeRoomId, activeOrderId, activeProfileUserId, activeArchiveRoundNumber, walletTargetAction, meSecurityTargetChannel, walletTicketsTargetView, walletTicketsResultId };
    if (replace) window.history.replaceState(state, '', url);
    else window.history.pushState(state, '', url);
  } catch {
    try {
      window.location.hash = hash;
    } catch {
      // Some embedded browsers can reject history writes; the in-memory route is still authoritative.
    }
  }
}

function buildHash(
  route: PlayerRoute,
  activeRoomId: string | null,
  activeOrderId: string | null,
  activeProfileUserId: string | null,
  activeArchiveRoundNumber: string | null,
  walletTargetAction: WalletTargetAction,
  meSecurityTargetChannel: MeSecurityTargetChannel,
  walletTicketsTargetView: WalletTicketsTargetView,
  walletTicketsResultId: string | null,
) {
  if (route === 'room') {
    const id = (activeRoomId || '').replace(/^room-/, '');
    return id ? `#/room/${id}` : '#/hall';
  }
  if (route === 'milhar') return '#/milhar';
  if (route === 'milhar.ledger') return '#/milhar/ledger';
  if (route === 'milhar.stats') return '#/milhar/stats';
  if (route === 'milhar.hora') return '#/milhar-hora';
  if (route === 'milhar.hora.ledger') return '#/milhar-hora/ledger';
  if (route === 'milhar.hora.stats') return '#/milhar-hora/stats';
  if (route === 'lobby.next') return '#/hall-next';
  if (route === 'lobby.legacy') return '#/hall-legacy';
  if (route === 'lobby') return '#/hall';
  if (route === 'wallet') return walletTargetAction ? `#/wallet/${walletTargetAction}` : '#/wallet';
  if (route === 'wallet.tickets') {
    if (walletTicketsTargetView === 'register') return '#/wallet/tickets/raffle';
    if (walletTicketsTargetView === 'participants') return '#/wallet/tickets/participants';
    if (walletTicketsTargetView === 'result' && walletTicketsResultId) return `#/wallet/tickets/result/${encodeURIComponent(walletTicketsResultId)}`;
    return '#/wallet/tickets';
  }
  if (route === 'pedidos') return activeOrderId ? `#/pedidos/${encodeURIComponent(activeOrderId)}` : '#/pedidos';
  if (route === 'pedidos.unpaid') return activeOrderId ? `#/pedidos/unpaid/${encodeURIComponent(activeOrderId)}` : '#/pedidos/unpaid';
  if (route === 'leaderboard') return activeProfileUserId ? `#/leaderboard/user/${encodeURIComponent(activeProfileUserId)}` : '#/leaderboard';
  if (route === 'archive') return activeArchiveRoundNumber ? `#/archive/round/${encodeURIComponent(activeArchiveRoundNumber)}` : '#/archive';
  if (route === 'me.security' && meSecurityTargetChannel) return `#/me/security/${meSecurityTargetChannel}`;
  if (route === 'me.history') return activeOrderId ? `#/me/history/${encodeURIComponent(activeOrderId)}` : '#/me/history';
  if (route.startsWith('me.')) return `#/me/${route.slice(3)}`;
  // 'avisos.faq' is a legacy alias kept for existing call sites (e.g. MePage's
  // support sheet) — it still serializes to its old hash so those links don't
  // change shape, even though parsing that hash now yields 'avisos.help' (see
  // normalizeRouteName). The canonical routes serialize/parse symmetrically.
  if (route === 'avisos.faq') return '#/avisos/faq';
  if (route === 'avisos.help') return '#/avisos/help';
  if (route === 'avisos.feedback') return '#/avisos/feedback';
  if (route === 'sorteios.history') return '#/sorteios/history';
  if (route === 'auth' || route === 'auth.login') return '#/auth.login';
  if (route === 'auth.register') return '#/auth.register';
  if (route === 'auth.forgot') return '#/auth.forgot';
  if (route === 'auth.username') return '#/auth.username';
  return `#/${route}`;
}

function persistNavigation(
  route: PlayerRoute,
  activeRoomId: string | null,
  activeOrderId: string | null,
  activeProfileUserId: string | null,
  activeArchiveRoundNumber: string | null,
  walletTargetAction: WalletTargetAction,
  meSecurityTargetChannel: MeSecurityTargetChannel,
  walletTicketsTargetView: WalletTicketsTargetView,
  walletTicketsResultId: string | null,
) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify({
      route,
      activeRoomId,
      currentRoomId: activeRoomId,
      activeOrderId: keepsActiveOrderId(route) ? activeOrderId : null,
      activeProfileUserId: route === 'leaderboard' ? activeProfileUserId : null,
      activeArchiveRoundNumber: route === 'archive' ? activeArchiveRoundNumber : null,
      walletTargetAction: route === 'wallet' ? walletTargetAction : null,
      meSecurityTargetChannel: route === 'me.security' ? meSecurityTargetChannel : null,
      walletTicketsTargetView: route === 'wallet.tickets' ? walletTicketsTargetView : null,
      walletTicketsResultId: route === 'wallet.tickets' && walletTicketsTargetView === 'result' ? walletTicketsResultId : null,
    }));
  } catch {
    // localStorage may be unavailable in hardened browser modes.
  }
}
